import { Relation } from '@cotera/era';
import { useCallback, useEffect, useState } from 'react';
import { useDuckDBQuery } from '@cotera/client/app/etc/data/duckdb';
import { Semaphore } from '@cotera/client/app/etc';
import { DataGridModel } from './model';
import { useWatchableValue } from '../actions/framework';

//separate rate limiter so that we're not blocked by headers loading
const rateLimiter = new Semaphore(4);

export const usePaginatedDuckDbQuery = (opts: {
  rel: Relation;
  take: number;
  model: DataGridModel;
}) => {
  const cacheKey = useWatchableValue(opts.model.cacheKey);
  const { data: duckdbData } = useDuckDBQuery({
    rel: opts.rel,
    rateLimiter,
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);

  useEffect(() => {
    setData(duckdbData.data.slice(0, opts.take + 1).toArray());
    opts.model.totalRows.set(duckdbData.data.numRows);
    opts.model.totalLoadedRows.set(
      Math.min(opts.take, duckdbData.data.numRows)
    );
    opts.model.data.set(duckdbData.data);
  }, [duckdbData, cacheKey, opts.take, opts.model]);

  const handleLoadMore = useCallback(async () => {
    if (loading) {
      return;
    }

    setLoading(true);

    setData((prev) => {
      const startIndex = prev.length;
      const endIndex = startIndex + opts.take;
      const newRows = duckdbData.data.slice(startIndex, endIndex).toArray();
      const newData = [...prev, ...newRows];

      // Update the model with the new total loaded rows
      opts.model.totalLoadedRows.set(newData.length);

      return newData;
    });

    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, cacheKey, opts.take]);

  return {
    data,
    loading,
    hasMore: data.length < duckdbData.data.numRows,
    loadMore: handleLoadMore,
    totalRows: duckdbData.data.numRows,
  };
};
